import { SceneObject, Behavior } from './index';

export type { SceneObject, Behavior };

// ===== Provenance (carried by every generatable artifact) =====
export interface Provenance {
  source: 'ai' | 'human' | 'hybrid';
  prompt?: string;
  provider?: string; // 'anthropic' | 'stability' | 'replicate' | ...
  model?: string;
  costCredits?: number;
  generatedAt?: string; // ISO
}

export interface SceneEnvironment {
  skyboxAssetId?: string;
  backgroundColor?: string;
  ambientLight?: number;
  directionalLight?: { intensity: number; position: { x: number; y: number; z: number } };
  fog?: { color: string; near: number; far: number };
}

// The game-model scene (named GameScene to avoid clashing with the legacy `Scene` in ./index)
export interface GameScene {
  id: string;
  name: string;
  objects: SceneObject[];
  environment: SceneEnvironment;
  cameraStart?: {
    position: { x: number; y: number; z: number };
    target: { x: number; y: number; z: number };
  };
  provenance?: Provenance;
}

// ===== Assets =====
export type AssetKind = 'image' | 'texture' | 'sprite' | 'model3d' | 'audio';
export interface Asset {
  id: string;
  name: string;
  kind: AssetKind;
  url: string;
  thumbnailUrl?: string;
  provenance: Provenance;
  metadata?: Record<string, unknown>;
}

// ===== Logic primitives (Behavior is defined in ./index and re-exported above) =====

// ===== Entity definitions (reusable templates) =====
export type EntityRole = 'player' | 'enemy' | 'npc' | 'item' | 'prop' | 'trigger';
export interface EntityDefinition {
  id: string;
  name: string;
  role: EntityRole;
  assetId?: string;
  primitive?: 'cube' | 'sphere' | 'plane';
  properties: Record<string, unknown>;
  behaviors: Behavior[];
  provenance: Provenance;
}

// ===== Narrative =====
export interface Character { id: string; name: string; bio?: string; assetId?: string; }
export interface DialogueNode { id: string; speaker: string; text: string; next?: string[]; }
export interface Quest { id: string; title: string; description: string; objectives: string[]; reward?: string; }
export interface Narrative {
  synopsis?: string;
  characters: Character[];
  dialogue: DialogueNode[];
  quests: Quest[];
  provenance?: Provenance;
}

// ===== Economy =====
export interface Currency { id: string; name: string; symbol: string; }
export interface GameItem {
  id: string;
  name: string;
  assetId?: string;
  price?: number;
  currencyId?: string;
  nftBinding?: string;
}
export interface Economy { currencies: Currency[]; items: GameItem[]; }

// ===== Concept / design =====
export interface GameConcept {
  pitch?: string;
  genre?: string;
  mechanics?: string[];
  artDirection?: string;
  targetAudience?: string;
  provenance?: Provenance;
}

// ===== Publish =====
export type PublishStatus = 'draft' | 'published' | 'archived';
export interface PublishInfo {
  status: PublishStatus;
  version: string;
  publishedAt?: string;
  deployUrl?: string;
}

// ===== The Game (top-level aggregate) =====
export interface Game {
  id: string;
  slug: string;
  title: string;
  description?: string;
  ownerId: string; // identity supplied by the ecosystem (Clerk uid)
  concept: GameConcept;
  scenes: GameScene[];
  entities: EntityDefinition[];
  assets: Asset[];
  narrative: Narrative;
  economy: Economy;
  settings: Record<string, unknown>;
  publish: PublishInfo;
  createdAt: string;
  updatedAt: string;
}

// ===== Unified generation envelope =====
export type GenerationTarget =
  | 'concept' | 'scene' | 'asset' | 'entity' | 'narrative' | 'behavior' | 'economy';

export interface GenerationRequest {
  gameId: string;
  target: GenerationTarget;
  prompt: string;
  mode: 'create' | 'edit';
  contextRef?: string;
  constraints?: Record<string, unknown>;
  providerOverride?: string;
}

export interface GenerationResult<T = unknown> {
  target: GenerationTarget;
  artifact: T;
  provenance: Provenance;
  warnings?: string[];
}

// ===== Builders (used by the store and on load/save) =====
export function emptyScene(partial?: Partial<GameScene>): GameScene {
  return {
    id: partial?.id ?? 'scene_1',
    name: partial?.name ?? 'Main Scene',
    objects: partial?.objects ?? [],
    environment: partial?.environment ?? { backgroundColor: '#1a1a1a', ambientLight: 0.6 },
    cameraStart: partial?.cameraStart,
    provenance: partial?.provenance,
  };
}

export function emptyGame(partial?: Partial<Game>): Game {
  const now = new Date().toISOString();
  return {
    id: partial?.id ?? '',
    slug: partial?.slug ?? '',
    title: partial?.title ?? 'Untitled Game',
    description: partial?.description,
    ownerId: partial?.ownerId ?? '',
    concept: partial?.concept ?? {},
    scenes: partial?.scenes && partial.scenes.length ? partial.scenes : [emptyScene()],
    entities: partial?.entities ?? [],
    assets: partial?.assets ?? [],
    narrative: partial?.narrative ?? { characters: [], dialogue: [], quests: [] },
    economy: partial?.economy ?? { currencies: [], items: [] },
    settings: partial?.settings ?? {},
    publish: partial?.publish ?? { status: 'draft', version: '0.1.0' },
    createdAt: partial?.createdAt ?? now,
    updatedAt: partial?.updatedAt ?? now,
  };
}
